import { Component, Input, OnChanges } from '@angular/core';
import {
  IndexPoint,
  IndexSeriesFile,
} from '../plate-detail-v2/components/market-info/market-index.model';

/* =============================================================================
   pla-dateless-index-chart — the dateless vs composite index chart of the
   Dateless Reference market band. Geometry and visual grammar ported
   faithfully from the approved v13 prototype chart (620×240 viewBox, grid at
   100..200, solid observed lines, dashed provisional final segment, no sale
   markers on the modelled line). Data comes from the bundled static asset at
   its native 2015 = 100 base; nothing is fetched here (the parent loads the
   asset once and passes it in).
   ========================================================================== */

interface ChartDot {
  cx: number;
  cy: number;
  cls: string;
}

interface ChartPath {
  d: string;
  cls: string;
}

interface ChartLabel {
  x: number;
  y: number;
  text: string;
  cls: string;
  anchor?: string;
  fill?: string;
}

const W = 620;
const H = 240;
const PAD_L = 32;
const PAD_R = 64;
const PAD_T = 14;
const PAD_B = 28;
const LO = 90;
const HI = 200;
const YEAR_MIN = 2015;

@Component({
  selector: 'pla-dateless-index-chart',
  templateUrl: './dateless-index-chart.component.html',
  styleUrls: ['./dateless-index-chart.component.scss'],
})
export class DatelessIndexChartComponent implements OnChanges {
  @Input() file: IndexSeriesFile | null = null;

  public gridLines = [100, 120, 140, 160, 180, 200];
  public paths: ChartPath[] = [];
  public dots: ChartDot[] = [];
  public labels: ChartLabel[] = [];
  public yearLabels: ChartLabel[] = [];
  public ariaLabel = '';
  public ready = false;

  ngOnChanges(): void {
    if (!this.file) return;
    const dateless = this.points('dateless');
    const composite = this.points('composite');
    if (!dateless.length || !composite.length) return;
    const years = dateless.map((p) => p.year);
    const P = years.length - 1;

    this.paths = [];
    this.dots = [];
    // composite drawn first so the dateless line sits on top (prototype order)
    this.buildSeries(composite, 'ln-mkt', P);
    this.buildSeries(dateless, 'ln-date', P);
    composite.forEach((p, i) => this.dots.push({ cx: this.x(i, P), cy: this.y(p.value), cls: 'dot-mkt' }));
    dateless.forEach((p, i) => this.dots.push({ cx: this.x(i, P), cy: this.y(p.value), cls: 'dot-date' }));

    this.yearLabels = years.map((yr, i) => ({
      x: this.x(i, P), y: H - 9, text: "'" + String(yr).slice(2), cls: 'axlab', anchor: 'middle',
    }));
    this.labels = [
      { x: this.x(P, P) + 8, y: this.y(dateless[P - 1].value) + 2, text: 'Dateless', cls: 'endlab', fill: 'var(--accent)' },
      { x: this.x(P, P) + 8, y: this.y(composite[P - 1].value) + 12, text: 'All plates', cls: 'endlab', fill: '#8f887b' },
      { x: PAD_L, y: PAD_T - 2, text: `${YEAR_MIN} = 100`, cls: 'axlab' },
    ];

    const last = dateless[P];
    const lastFull = dateless[P - 1];
    this.ariaLabel =
      `Dateless versus all-plate index, ${YEAR_MIN} to ${last.year}, base ${YEAR_MIN} equals 100. ` +
      `Dateless reached ${lastFull.value.toFixed(1)} in ${lastFull.year}; ` +
      `the ${last.year} value of ${last.value.toFixed(1)} is provisional. ` +
      `The all-plate composite reached ${composite[P - 1].value.toFixed(1)} in ${composite[P - 1].year}.`;
    this.ready = true;
  }

  /**
   * Series from YEAR_MIN, rebased so YEAR_MIN = 100. The published index (v2)
   * is already based 2015 = 100, so this is a no-op today; it keeps the chart
   * reading growth since YEAR_MIN if the published base ever moves.
   */
  private points(series: string): IndexPoint[] {
    const s = this.file && this.file.series[series];
    if (!s) return [];
    const pts = s.points.filter((p) => p.year >= YEAR_MIN);
    const base = pts.length && pts[0].year === YEAR_MIN ? pts[0].value : null;
    if (!base) return [];
    return pts.map((p) => ({ ...p, value: (p.value / base) * 100 }));
  }

  private x(i: number, P: number): number {
    return PAD_L + (i / P) * (W - PAD_L - PAD_R);
  }

  private y(v: number): number {
    return PAD_T + (1 - (v - LO) / (HI - LO)) * (H - PAD_T - PAD_B);
  }

  public yFor(v: number): number {
    return this.y(v);
  }

  /** Solid path to the last full year; dashed provisional final segment. */
  private buildSeries(pts: IndexPoint[], cls: string, P: number): void {
    let d = '';
    for (let i = 0; i <= P - 1; i++) {
      d += (i ? ' L ' : 'M ') + this.x(i, P) + ' ' + this.y(pts[i].value);
    }
    this.paths.push({ d, cls });
    this.paths.push({
      d: 'M ' + this.x(P - 1, P) + ' ' + this.y(pts[P - 1].value) +
         ' L ' + this.x(P, P) + ' ' + this.y(pts[P].value),
      cls: cls + ' ln-prov',
    });
  }
}
